import { useState, useCallback, useEffect } from 'react'
import moment from 'moment'
import api from '../../../services/api'

const useDateFilter = (initialFilter = '5', onFilterChange) => {
  const [dateFilter, setDateFilter] = useState(initialFilter)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // User table state
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [tableFilter, setTableFilter] = useState('All Time')
  const [selectedType, setSelectedType] = useState('')
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userData, setUserData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)

  // Credits table state
  const [creditsOffset, setCreditsOffset] = useState(0)
  const [creditsLimit, setCreditsLimit] = useState(10)
  const [creditsData, setCreditsData] = useState([])
  const [creditsLoading, setCreditsLoading] = useState(false)
  const [creditsError, setCreditsError] = useState(null)
  const [creditsTotal, setCreditsTotal] = useState(0)
  const [creditsSearchTerm, setCreditsSearchTerm] = useState('')
  const [debouncedCreditsSearchTerm, setDebouncedCreditsSearchTerm] = useState('')

  // Debounce search term for costs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Debounce search term for credits
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCreditsSearchTerm(creditsSearchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [creditsSearchTerm])

  const handleDateFilterChange = useCallback(
    value => {
      setDateFilter(value)
      setOffset(0) // Reset pagination on filter change
      setCreditsOffset(0)
      if (value !== '2') {
        // Reset custom dates when not using 'other' option
        setDateFrom('')
        setDateTo('')
      }
      if (onFilterChange) {
        onFilterChange(value)
      }
    },
    [onFilterChange]
  )

  const handleDateRangeChange = useCallback(
    (dates, dateStrings) => {
      if (dateStrings && Array.isArray(dateStrings) && dateStrings.length === 2) {
        setDateFrom(dateStrings[0])
        setDateTo(dateStrings[1])
        setOffset(0)
        setCreditsOffset(0)
      } else if (dates && Array.isArray(dates) && dates.length === 2) {
        setDateFrom(dates[0] ? dates[0].format('YYYY-MM-DD') : '')
        setDateTo(dates[1] ? dates[1].format('YYYY-MM-DD') : '')
        setOffset(0)
        setCreditsOffset(0)
      } else {
        setDateFrom('')
        setDateTo('')
      }
    },
    []
  )

  const handleSearch = useCallback((value) => {
    setSearchTerm(value)
    setOffset(0)
  }, [])

  const handleCreditsSearch = useCallback((value) => {
    setCreditsSearchTerm(value)
    setCreditsOffset(0)
  }, [])

  const handleTableFilterChange = useCallback((value) => {
    setTableFilter(value)
    setOffset(0)
    setCreditsOffset(0)
  }, [])

  const handlePageChange = useCallback((page, pageSize) => {
    setOffset((page - 1) * pageSize)
    setLimit(pageSize)
  }, [])

  const handleCreditsPageChange = useCallback((page, pageSize) => {
    setCreditsOffset((page - 1) * pageSize)
    setCreditsLimit(pageSize)
  }, [])

  const fetchUsers = useCallback(async () => {
    if (!selectedType) {
      setUserData([])
      setTotal(0)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setUserData([]) // Clear previous data so it doesn't "stack" visually before the new data arrives

    // Map time filter labels to API values
    const mapFilter = (label) => {
      switch (label) {
        case 'All Time': return 'allTime'
        case 'Today': return 'today'
        case 'This Week': return 'week'
        case 'This Month': return 'month'
        case 'This Year': return 'year'
        default: return ''
      }
    }

    // Determine the date filter to send
    const selectedDateFilter = mapFilter(tableFilter) ||
      (dateFilter === 'other' || dateFilter === '2' || dateFilter === '5' ? 'allTime' : dateFilter)

    try {
      const response = await api.reports.getUsageReportCosts({
        offset,
        limit,
        type: selectedType,
        search: debouncedSearchTerm,
        date: selectedDateFilter,
        from: dateFrom,
        to: dateTo
      })

      const responseData = response?.data || response || {}

      // Dynamically find the records array based on the selected type (e.g., courses, mazes, etc.)
      const typePlural = selectedType ? `${selectedType}s` : ''
      const rawRecords = (typePlural && responseData[typePlural]) || responseData.result || responseData.data || responseData.users || []

      const mappedData = Array.isArray(rawRecords) ? rawRecords.map(item => {
        // Use provided creator names if available, otherwise fallback to item name/title
        const userObj = item.user
        const creatorName = userObj
          ? `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim()
          : null

        const displayName = creatorName || item.title || item.name || item.full_name || 'Unknown'
        const displayAvatar = userObj?.avatar?.link || item.avatar || item.profile_image || ''

        return {
          id: item.id || item.user_id || userObj?.id,
          name: displayName,
          email: userObj?.email || '',
          avatar: displayAvatar,
          totalCreditsUsed: item.avg_cost || 0,
          model: item.model || '',
        }
      }) : []

      setUserData(mappedData)
      setTotal(responseData.total)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching credit usage data:', err)
      setError(err.message || 'Failed to fetch data')
      setLoading(false)
    }
  }, [dateFilter, dateFrom, dateTo, debouncedSearchTerm, tableFilter, offset, limit, selectedType])

  const fetchCredits = useCallback(async () => {
    setCreditsLoading(true)
    setCreditsError(null)
    setCreditsData([])

    // Map time filter labels to API values
    const mapFilter = (label) => {
      switch (label) {
        case 'All Time': return 'allTime'
        case 'Today': return 'today'
        case 'This Week': return 'week'
        case 'This Month': return 'month'
        case 'This Year': return 'year'
        default: return ''
      }
    }

    // Determine the date filter to send
    const selectedDateFilter = mapFilter(tableFilter) ||
      (dateFilter === 'other' || dateFilter === '2' || dateFilter === '5' ? 'allTime' : dateFilter)

    try {
      const response = await api.reports.getUsageReportCreditsService({
        offset: creditsOffset,
        limit: creditsLimit,
        search: debouncedCreditsSearchTerm,
        date: selectedDateFilter,
        from: dateFrom,
        to: dateTo
      })

      const responseData = response?.data || response || {}

      const rawRecords = responseData.users || []

      const mappedData = Array.isArray(rawRecords) ? rawRecords.map(item => {
        // Use provided creator names if available, otherwise fallback to item name/title
        const userObj = item.creator
        const creatorName = userObj
          ? `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim()
          : null

        const displayAvatar = userObj?.avatar?.link || ''

        return {
          id: item.id || item.user_id || userObj?.id,
          name: creatorName,
          rank: userObj?.rank || '',
          avatar: displayAvatar,
          totalCreditsUsed: item.total_used_credits || 0,
        }
      }) : []

      setCreditsData(mappedData)
      setCreditsTotal(responseData.total)
      setCreditsLoading(false)
    } catch (err) {
      console.error('Error fetching credit usage credits data:', err)
      setCreditsError(err.message || 'Failed to fetch data')
      setCreditsLoading(false)
    }
  }, [dateFilter, dateFrom, dateTo, debouncedCreditsSearchTerm, tableFilter, creditsOffset, creditsLimit])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    fetchCredits()
  }, [fetchCredits])

  return {
    dateFilter,
    dateFrom,
    dateTo,
    setDateFilter: handleDateFilterChange,
    setDateRange: handleDateRangeChange,
    isCustomDate: dateFilter === '2',

    // User table data and handlers
    userData,
    userLoading: loading,
    userError: error,
    userTotal: total,
    searchTerm,
    tableFilter,
    setTableFilter: handleTableFilterChange,
    selectedType,
    setSelectedType: (type) => {
      setSelectedType(type)
      setOffset(0)
    },
    pagination: {
      current: Math.floor(offset / limit) + 1,
      pageSize: limit,
      total: total
    },
    creditsPagination: {
      current: Math.floor(creditsOffset / creditsLimit) + 1,
      pageSize: creditsLimit,
      total: creditsTotal
    },
    onSearch: handleSearch,
    onCreditsSearch: handleCreditsSearch,
    onPageChange: handlePageChange,
    onCreditsPageChange: handleCreditsPageChange,
    refetchUsers: fetchUsers,
    refetchCredits: fetchCredits,
    creditsData,
    creditsLoading,
    creditsError,
    creditsTotal,
    creditsSearchTerm
  }
}

export default useDateFilter
